import { parseConfigResponse, type RuntimeSupabaseConfig } from './supabaseConfig.ts';

/**
 * Stale-while-revalidate storage for the /api/config boot answer.
 *
 * The boot gate (supabase.ts initSupabaseConfig) used to hold EVERY first paint
 * behind one /api/config round trip — about a second, because each API request
 * pays several serial Supabase hops server-side, and the API sends no-store so
 * the browser cannot cache it either. The answer almost never changes for an
 * origin, so a repeat visit renders from the copy kept here and the fetch runs
 * behind the render. A cold visit (nothing cached) still blocks as before.
 *
 * Kept free of fetch and React so the rules are testable in node.
 */

export type ConfigStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/**
 * Versioned, and namespaced by ORIGIN even though localStorage already is: on
 * the legacy shared base the admin and portal SPAs share an origin, and keying
 * the value says plainly that the answer belongs to the hostname, never to the
 * user or the app.
 */
export function configCacheKey(origin: string): string {
  return `l2u-runtime-config-v1:${origin}`;
}

/** sessionStorage flag that stops a config-change reload from looping. */
export const CONFIG_RELOAD_FLAG = 'l2u-runtime-config-reloaded';

export function readCachedConfig(
  storage: ConfigStorage | null,
  origin: string,
): RuntimeSupabaseConfig | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(configCacheKey(origin));
    if (!raw) return null;
    // Re-validated with the same parser as a live response, so a corrupt or
    // older-shaped entry is a cache miss (a cold, blocking boot) rather than a
    // half-built config.
    return parseConfigResponse(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function writeCachedConfig(
  storage: ConfigStorage | null,
  origin: string,
  config: RuntimeSupabaseConfig,
): void {
  if (!storage) return;
  try {
    storage.setItem(configCacheKey(origin), JSON.stringify(config));
  } catch {
    // Quota or blocked storage: the next load is a cold one, nothing worse.
  }
}

export function clearCachedConfig(storage: ConfigStorage | null, origin: string): void {
  if (!storage) return;
  try {
    storage.removeItem(configCacheKey(origin));
  } catch {
    // Unwritable storage cannot have held a cache entry worth removing either.
  }
}

/**
 * True when two configs select the same auth project and the same routing.
 *
 * Every field here decides something that is fixed at first render and cannot
 * be swapped underneath a mounted app: url/publishableKey/ref pick the auth pool
 * and the session's storage key, hostMode fixes the admin router's basename,
 * and audience opens or closes the portal's path families. signInUrls is
 * deliberately absent: it only feeds two hrefs, and a stale pair is corrected by
 * the next load without a reload.
 */
export function sameBootTarget(a: RuntimeSupabaseConfig, b: RuntimeSupabaseConfig): boolean {
  return (
    a.url === b.url &&
    a.publishableKey === b.publishableKey &&
    a.ref === b.ref &&
    a.hostMode === b.hostMode &&
    a.audience === b.audience
  );
}

export type ReloadGuardStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/**
 * Reload the page at most once per tab for a config change.
 *
 * The reload is what turns a stale render into a correct one, and it can only
 * help if the next boot is COLD — the caller clears the cache first. If that
 * clear silently failed, the next boot would render the same stale copy and ask
 * to reload again; the flag breaks that cycle (the second ask is refused and
 * the flag dropped, so a later genuine change can reload again). Returns
 * whether a reload was triggered.
 *
 * A storage that cannot be read at all refuses: without the flag there is no
 * loop protection, and an endless reload is worse than one stale render.
 */
export function reloadOnce(session: ReloadGuardStorage | null, reload: () => void): boolean {
  if (!session) return false;
  try {
    if (session.getItem(CONFIG_RELOAD_FLAG)) {
      session.removeItem(CONFIG_RELOAD_FLAG);
      return false;
    }
    session.setItem(CONFIG_RELOAD_FLAG, '1');
  } catch {
    return false;
  }
  reload();
  return true;
}

/**
 * The storage, if reloadOnce can really use it as its loop guard; else null.
 *
 * Existing is not enough: a sessionStorage that refuses writes (quota, older
 * Safari private mode) makes reloadOnce refuse every reload, so a cached boot
 * on it could never correct a stale project. Probed with a real write under a
 * key of its own; the boot treats null as "no guard" and boots cold.
 */
export function usableReloadGuard(
  session: ReloadGuardStorage | null | undefined,
): ReloadGuardStorage | null {
  if (!session) return null;
  const probe = `${CONFIG_RELOAD_FLAG}:probe`;
  try {
    session.setItem(probe, '1');
    session.removeItem(probe);
    return session;
  } catch {
    return null;
  }
}

/** Called once a boot has proven its config current, re-arming reloadOnce. */
export function clearReloadFlag(session: ReloadGuardStorage | null): void {
  if (!session) return;
  try {
    session.removeItem(CONFIG_RELOAD_FLAG);
  } catch {
    // Nothing to re-arm on storage we cannot touch; reloadOnce refuses there.
  }
}
